/**
 * Onboarding/help persistence. Uses localStorage directly (the app's
 * convention — there is no user-preferences backend). All access is wrapped so
 * private-mode / quota failures degrade to "never seen" without throwing.
 *
 * Tour state is keyed by route and stores the guide *version* that was last
 * seen, so bumping a guide's `version` re-triggers the first-run tour for
 * everyone. Guided-action dismissals are tracked per route + action id.
 */

type MinimalStorage = Pick<Storage, "getItem" | "setItem">;

function defaultStorage(): MinimalStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

const tourKey = (route: string) => `l30:onboarding:${route}`;
const actionsKey = (route: string) => `l30:nextactions:${route}`;

/**
 * Setting this localStorage key suppresses ALL first-run tours. Used by E2E
 * smokes (so onboarding doesn't block other features) and available as a
 * "disable tips" escape hatch.
 */
export const ONBOARDING_DISABLED_KEY = "l30:onboarding:disabled";

function isOnboardingDisabled(storage: MinimalStorage | null): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(ONBOARDING_DISABLED_KEY) !== null;
  } catch {
    return false;
  }
}

interface TourRecord {
  seenVersion: number;
}

function readTour(route: string, storage: MinimalStorage | null): TourRecord | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(tourKey(route));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TourRecord>;
    return typeof parsed.seenVersion === "number" ? { seenVersion: parsed.seenVersion } : null;
  } catch {
    return null;
  }
}

/**
 * Whether the first-run tour should auto-open: never seen, or the guide has a
 * newer version than the one last seen.
 */
export function shouldAutoStartTour(
  route: string,
  version: number,
  storage: MinimalStorage | null = defaultStorage(),
): boolean {
  if (isOnboardingDisabled(storage)) return false;
  const record = readTour(route, storage);
  return record === null || version > record.seenVersion;
}

/** Record that the tour for this route+version has been seen (or dismissed). */
export function markTourSeen(
  route: string,
  version: number,
  storage: MinimalStorage | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(tourKey(route), JSON.stringify({ seenVersion: version } satisfies TourRecord));
  } catch {
    // best-effort; a write failure simply means the tour may show again
  }
}

/** Guided-action ids the user has dismissed on this route. */
export function getDismissedActions(
  route: string,
  storage: MinimalStorage | null = defaultStorage(),
): string[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(actionsKey(route));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

/** Mark a guided action dismissed so it isn't suggested again on this route. */
export function dismissAction(
  route: string,
  actionId: string,
  storage: MinimalStorage | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    const current = getDismissedActions(route, storage);
    if (current.includes(actionId)) return;
    storage.setItem(actionsKey(route), JSON.stringify([...current, actionId]));
  } catch {
    // best-effort
  }
}
